<template>
  <div :class="{ 'cursor-wait': isUploading }">
    <div class="grid gap-4">
      <AppSelect
        :mode="mode"
        title="“Append” adds new items. “Overwrite” replaces your current inventory."
        v-model="uploadMode"
      >
        <template #options>
          <option selected value="append">Append</option>
          <option value="overwrite">Overwrite</option>
        </template>
      </AppSelect>
      <div class="w-[200px] sm:w-full">
        <input
          type="file"
          id="attach-file"
          class="hidden"
          ref="fileInput"
          accept=".csv"
          @change="handleFileSelect"
        />
        <label
          for="attach-file"
          class="button secondary-button gap-2"
          :disabled="isUploading"
        >
          <Paperclip :size="20" :stroke-width="1.75" />
          Attach file
        </label>
      </div>
      <div
        v-if="selectedFile"
        class="relative grid grid-cols-3 gap-3 rounded-lg border p-3 sm:grid-cols-1"
        :class="[mode === 'dark' ? 'border-dark-border' : 'border-border-10']"
      >
        <div
          class="rounded-lg bg-opacity-50 p-4 text-center"
          :class="[mode === 'dark' ? 'bg-dark-border' : 'bg-black-10']"
        >
          <span
            class="mb-1 block text-[14px] font-medium leading-none"
            :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-80']"
            >Name</span
          >
          <span
            v-tooltip="`${selectedFile.name}`"
            class="mx-auto block max-w-[150px] truncate text-[14px] font-semibold"
            >{{ selectedFile.name }}</span
          >
        </div>
        <div
          class="rounded-lg bg-opacity-50 p-4 text-center"
          :class="[mode === 'dark' ? 'bg-dark-border' : 'bg-black-10']"
        >
          <span
            class="mb-1 block text-[14px] font-medium leading-none"
            :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-80']"
            >Size</span
          >
          <span class="text-[14px] font-semibold">{{
            formatFileSize(selectedFile.size)
          }}</span>
        </div>
        <div
          class="rounded-lg bg-opacity-50 p-4 text-center"
          :class="[mode === 'dark' ? 'bg-dark-border' : 'bg-black-10']"
        >
          <span
            class="mb-1 block text-[14px] font-medium leading-none"
            :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-80']"
            >Type</span
          >
          <span class="text-[14px] font-semibold">{{ selectedFile.type }}</span>
        </div>
        <div class="col-span-3 sm:col-span-1">
          <AppButton
            title="Delete file"
            @action="deleteSelectedFile"
            type="danger"
          />
        </div>
      </div>
    </div>
    <div class="mt-6 sm:mt-4">
      <AppButton
        :title="isUploading ? 'Uploading...' : 'Powerload (Upload your File)'"
        @action="uploadFile"
        :isDisabled="!selectedFile || isUploading"
      />
    </div>
  </div>
</template>

<script>
import { Paperclip } from "lucide-vue-next";
import AppSelect from "@C/Form/AppSelect.vue";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useGlobalErrorHandler } from "@L/errorHandler";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "UploadForm",
  props: {
    mode: String,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      handleError,
    };
  },
  components: {
    Paperclip,
    AppSelect,
    AppButton,
  },
  data() {
    return {
      selectedFile: null,
      isUploading: false,
      uploadMode: "append",
    };
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      this.selectedFile = file;
    },
    async uploadFile() {
      this.isUploading = true;
      try {
        if (!this.selectedFile) {
          this.toastStore.show("Please select a file first", "error");
          return;
        }
        const formData = new FormData();
        formData.append("file", this.selectedFile);
        formData.append("mode", this.uploadMode);
        const response = await fetch("/member/upload", {
          method: "POST",
          body: formData,
        });
        if (response.ok) {
          await response.text();
          this.toastStore.show("File uploaded successfully!", "success");
          this.resetForm();
        } else {
          const errorText = await response.text();
          this.toastStore.show(`Upload failed: ${errorText}`, "error");
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.isUploading = false;
      }
    },
    resetForm() {
      this.selectedFile = null;
      this.$refs.fileInput.value = "";
      this.uploadMode = "append";
    },
    deleteSelectedFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = "";
    },
    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
  },
};
</script>
<style scoped>
.cursor-wait,
.cursor-wait * {
  cursor: wait !important;
}
</style>